import React from "react";
import { motion } from "framer-motion";
import {
  FiClock,
  FiDollarSign,
  FiMapPin,
  FiShield,
  FiTruck,
  FiUsers,
} from "react-icons/fi";

const features = [
  {
    number: "01",
    icon: FiTruck,
    title: "Every Vehicle, One App",
    description:
      "Book Auto, Cab, Bike, Tempo, Mini Truck, Pickup, Tractor, JCB and Ambulance from one app.",
  },
  {
    number: "02",
    icon: FiShield,
    title: "Verified Drivers",
    description:
      "Every driver goes through verification for safer rides and trusted transport.",
  },
  {
    number: "03",
    icon: FiClock,
    title: "Instant Booking",
    description:
      "Find nearby drivers in seconds with real-time availability.",
  },
  {
    number: "04",
    icon: FiMapPin,
    title: "Live Tracking",
    description:
      "Track your ride and goods from pickup to destination.",
  },
  {
    number: "05",
    icon: FiDollarSign,
    title: "Zero Commission",
    description:
      "Drivers keep what they earn with no hidden commission charges.",
  },
  {
    number: "06",
    icon: FiUsers,
    title: "Built For Everyone",
    description:
      "Passengers, businesses and drivers all use the same platform.",
  },
];

const leftFeatures = features.slice(0, 3);
const rightFeatures = features.slice(3);

const cardVariants = {
  hidden: { opacity: 0, y: 34 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const listVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

function FeatureCard({ feature }) {
  const Icon = feature.icon;

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{
        scale: 1.05,
        y: -8,
        rotate: feature.number === "02" || feature.number === "05" ? -1 : 1,
      }}
      className="group rounded-3xl border border-trogoOrange/20 bg-white p-6 shadow-[0_22px_70px_rgba(120,72,28,0.08)] transition hover:shadow-[0_30px_90px_rgba(255,122,0,0.18)]"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="text-5xl font-black leading-none text-trogoOrange/20 transition group-hover:text-trogoOrange/40">
          {feature.number}
        </span>
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-trogoOrange/10 text-trogoOrange shadow-inner">
          <Icon className="text-2xl" />
        </span>
      </div>

      <h3 className="mt-6 text-xl font-bold leading-snug text-slate-950">
        {feature.title}
      </h3>
      <p className="mt-3 text-sm leading-7 text-slate-600">
        {feature.description}
      </p>
    </motion.div>
  );
}

function PhoneMockup() {
  return (
    <motion.div
      animate={{ y: [0, -18, 0], rotate: [-1.2, 1.2, -1.2] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="relative mx-auto w-[278px] sm:w-[320px]"
    >
      <div className="absolute inset-x-8 bottom-[-36px] h-16 rounded-full bg-black/20 blur-2xl" />
      <div className="rounded-[40px] bg-black p-3 shadow-2xl shadow-trogoOrange/10">
        <div className="overflow-hidden rounded-[32px] bg-gradient-to-br from-trogoOrange via-[#ff9d3f] to-[#fff1e4] p-4">
          <div className="mx-auto mb-4 h-1.5 w-16 rounded-full bg-black/25" />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/80">
                TROGO
              </p>
              <h3 className="text-2xl font-black text-white">Book a ride</h3>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/25 text-white backdrop-blur-xl">
              <FiUsers className="text-xl" />
            </div>
          </div>

          <div className="mt-5 rounded-2xl bg-white/95 px-4 py-3 text-sm font-semibold text-slate-500 shadow-lg">
            Search vehicle or destination
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            {[
              ["Auto", FiTruck],
              ["Cab", FiUsers],
              ["Goods", FiDollarSign],
              ["JCB", FiClock],
            ].map(([label, Icon]) => (
              <div
                key={label}
                className="rounded-2xl bg-white/85 p-3 shadow-sm backdrop-blur-xl"
              >
                <Icon className="text-xl text-trogoOrange" />
                <p className="mt-2 text-sm font-bold text-slate-900">
                  {label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 overflow-hidden rounded-3xl bg-white/90 p-3 shadow-lg">
            <div className="relative h-28 overflow-hidden rounded-2xl bg-slate-100">
              {/* Map Grid */}
              <div className="absolute inset-0 opacity-60">
                <div className="absolute top-0 left-1/4 h-full w-px bg-slate-200"></div>
                <div className="absolute top-0 left-2/4 h-full w-px bg-slate-200"></div>
                <div className="absolute top-0 left-3/4 h-full w-px bg-slate-200"></div>
                <div className="absolute left-0 top-1/4 h-px w-full bg-slate-200"></div>
                <div className="absolute left-0 top-2/4 h-px w-full bg-slate-200"></div>
                <div className="absolute left-0 top-3/4 h-px w-full bg-slate-200"></div>
              </div>

              {/* Building Blocks */}
              <div className="absolute top-[10%] left-[5%] h-5 w-10 rounded bg-slate-200/80"></div>
              <div className="absolute top-[30%] left-[30%] h-8 w-12 rounded bg-slate-200/80"></div>
              <div className="absolute bottom-[15%] right-[10%] h-10 w-6 rounded bg-slate-200/80"></div>

              {/* Route */}
              <svg
                viewBox="0 0 100 100"
                className="absolute inset-0 h-full w-full"
              >
                <path
                  d="M 20 25 Q 40 50, 50 50 T 80 75"
                  strokeDasharray="3 2"
                  className="stroke-trogoBlue/80"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>

              {/* Markers */}
              <div className="absolute left-[18%] top-[22%] h-2.5 w-2.5 rounded-full bg-trogoOrange ring-2 ring-white shadow-md" />
              <div className="absolute right-[22%] bottom-[22%] h-2.5 w-2.5 rounded-full bg-slate-900 ring-2 ring-white shadow-md" />
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-slate-950 py-3 text-center text-sm font-black text-white shadow-xl">
            Confirm Booking
          </div>
          <div className="mt-5 flex items-center justify-around rounded-2xl bg-white/60 px-4 py-3 text-trogoOrange backdrop-blur-xl">
            <FiTruck />
            <FiMapPin />
            <FiClock />
            <FiUsers />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function WhyTrogo() {
  return (
    <section className="relative isolate overflow-hidden bg-trogoOrange/5 py-24 sm:py-28">
      <div className="absolute left-[-10rem] top-24 h-80 w-80 rounded-full bg-trogoOrange/10 blur-3xl" />
      <div className="absolute right-[-8rem] top-1/3 h-72 w-72 rounded-full bg-trogoOrange/10 blur-3xl" />
      <div className="absolute bottom-[-9rem] left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-trogoOrange/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="mx-auto max-w-4xl text-center"
        >
          <span className="inline-flex rounded-full border border-trogoOrange/20 bg-white px-5 py-2 text-xs font-bold uppercase tracking-[0.28em] text-trogoOrange shadow-sm">
            WHY CHOOSE TROGO
          </span>

          <h2 className="mt-6 text-4xl font-black leading-[1.05] tracking-normal text-slate-950 sm:text-5xl lg:text-6xl">
            One Intelligent App.
            <span className="block text-trogoOrange">
              Every Transport Need.
            </span>
          </h2>

          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-600">
            From everyday rides to heavy-duty transport, TROGO brings every
            vehicle and every journey together in one powerful platform built
            for speed, safety and convenience.
          </p>
        </motion.div>

        <div className="mt-18 grid items-center gap-8 lg:mt-20 lg:grid-cols-[1fr_0.9fr_1fr]">
          <motion.div
            variants={listVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="order-2 grid gap-5 lg:order-1"
          >
            {leftFeatures.map((feature) => (
              <FeatureCard key={feature.number} feature={feature} />
            ))}
          </motion.div>

          <div className="order-1 lg:order-2">
            <PhoneMockup />
          </div>

          <motion.div
            variants={listVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="order-3 grid gap-5"
          >
            {rightFeatures.map((feature) => (
              <FeatureCard key={feature.number} feature={feature} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
